import { useCallback, useEffect, useRef, useState } from 'react';

import type { PricingOptionIndex } from '../../types';

import {
  BASIC_CATEGORY_INDEX,
  MAX_LOS,
  MIN_LOS,
  SOLO_CATEGORY_INDEX,
  SOLO_UPGRADE_CATEGORY_INDEX,
  UPGRADE_CATEGORY_INDEX,
} from './packageCategories';
import {
  ASIA_ONLY_LOS,
  DEFAULT_OPTION_STATE,
  adjustSellKeepingMargin,
  applyNetFormulaToPricing,
  clearNonAsiaOnlyPricing,
  defaultRoomPricing,
  deriveExtraNightSell,
  extraNightFromNewBaseLos,
  isRoomInNetFormulaScope,
  netAtAnchorMargin,
  newPackageOptionId,
  prorateSellByLos,
  reapplyNetFormula,
  setSellPrice,
} from './packageOptionsModel';
import type { SellAdjustment } from './packageOptionsModel';
import { ParsedRoomRow, matchRoomAsset } from './parsePastedOffer';
import type {
  AttachedExperience,
  CategoryRoom,
  CreatedAsset,
  NetRateFormulaState,
  OptionState,
  PackageOptionId,
  PackageOptionsDraft,
  RoomAssetLink,
  RoomPricing,
  UpliftType,
} from './types';

export type { AttachedExperience, CategoryRoom, UpliftType } from './types';

// Experience cost never touches net: net is the base room's own net plus the flat manual increase.
export function upgradePerLos(sellIncrease: number, netIncrease: number) {
  return (sell: number, net: number) => {
    const newSell = sell > 0 ? Math.round(sell + sellIncrease) : 0;
    return { sell: newSell, net: net + netIncrease };
  };
}

export function soloPerLos(rateBasis: 'per_stay' | 'per_night', sellDecrease: number, netDecrease: number) {
  return (sell: number, net: number, nights: number) => {
    const scale = rateBasis === 'per_night' ? nights : 1;
    return {
      sell: sell > 0 ? Math.max(0, Math.round(sell - sellDecrease * scale)) : 0,
      net: Math.max(0, net - netDecrease * scale),
    };
  };
}

export function resolveUpliftType(
  hasExperiences: boolean,
  propertySellIncrease: number,
  netIncrease: number,
): UpliftType {
  if (!hasExperiences) {
    return 'property_net';
  }
  return propertySellIncrease > 0 || netIncrease > 0 ? 'combined' : 'bundled_experience';
}

export function usePackageOptionsState(offerId: string | undefined) {
  const [activeOption, setActiveOption] = useState<PricingOptionIndex>(0);
  const [optionStates, setOptionStates] = useState<Record<number, OptionState>>({
    0: DEFAULT_OPTION_STATE,
  });
  const [roomAssets, setRoomAssets] = useState<Array<CreatedAsset>>([]);
  const activeOptionRef = useRef(activeOption);
  activeOptionRef.current = activeOption;

  const optionState = optionStates[activeOption] ?? DEFAULT_OPTION_STATE;

  useEffect(() => {
    setActiveOption(0);
    setOptionStates({ 0: DEFAULT_OPTION_STATE });
    setRoomAssets([]);
  }, [offerId]);

  const updateActiveOption = useCallback((updater: (state: OptionState) => OptionState) => {
    setOptionStates((prev) => {
      const option = activeOptionRef.current;
      return { ...prev, [option]: updater(prev[option] ?? DEFAULT_OPTION_STATE) };
    });
  }, []);

  const updateCategoryRooms = useCallback(
    (categoryIndex: number, updater: (rooms: Array<CategoryRoom>) => Array<CategoryRoom>) => {
      updateActiveOption((state) => ({
        ...state,
        roomsByCategory: state.roomsByCategory.map((rooms, index) =>
          index === categoryIndex ? updater(rooms) : rooms,
        ),
      }));
    },
    [updateActiveOption],
  );

  const addRoomType = useCallback(
    (categoryIndex: number, asset?: RoomAssetLink) => {
      updateCategoryRooms(categoryIndex, (rooms) => {
        const emptyRoom = rooms.find((room) => !room.assetId);
        if (asset && emptyRoom) {
          return rooms.map((room) =>
            room.id === emptyRoom.id ? { ...room, assetId: asset.id, assetName: asset.name } : room,
          );
        }
        const id = newPackageOptionId();
        return [
          ...rooms,
          {
            id,
            assetId: asset?.id ?? '',
            assetName: asset?.name ?? '',
            asiaOnly: false,
          },
        ];
      });
    },
    [updateCategoryRooms],
  );

  const copyFromFirstOption = useCallback((adjustment: SellAdjustment = { mode: 'none', amount: 0 }) => {
    setOptionStates((prev) => {
      const sourceState = prev[0] ?? DEFAULT_OPTION_STATE;
      const idRemap = new Map<PackageOptionId, PackageOptionId>();
      const roomsByCategory = sourceState.roomsByCategory.map((rooms) =>
        rooms.map((room) => {
          const newId = newPackageOptionId();
          idRemap.set(room.id, newId);
          return { ...room, id: newId };
        }),
      );

      return {
        ...prev,
        [activeOptionRef.current]: {
          ...sourceState,
          roomsByCategory,
          pricingByRoomId: Object.fromEntries(
            Object.entries(sourceState.pricingByRoomId).map(([id, pricing]) => {
              const sellByLos = { ...pricing.sellByLos };
              const netByLos = { ...pricing.netByLos };
              for (const [losKey, sellValue] of Object.entries(pricing.sellByLos)) {
                const nights = Number(losKey);
                const sell = Number(sellValue) || 0;
                const net = Number(pricing.netByLos[nights]) || 0;
                const adjusted = adjustSellKeepingMargin(sell, net, { ...adjustment, nights });
                sellByLos[nights] = String(adjusted.sell);
                netByLos[nights] = String(adjusted.net);
              }
              const extraNightSell = deriveExtraNightSell({ ...pricing, sellByLos }, sourceState.lengthsOfStay);
              let extraNightNet = pricing.extraNightNet;
              if (!pricing.extraNightSellEdited) {
                const [baseLos] = sourceState.lengthsOfStay;
                const recomputed = extraNightFromNewBaseLos(
                  Number(sellByLos[baseLos]) || 0,
                  Number(netByLos[baseLos]) || 0,
                  baseLos,
                );
                extraNightNet = String(recomputed.net);
              }

              const newId = idRemap.get(id) ?? id;
              return [newId, { ...pricing, sellByLos, netByLos, extraNightSell, extraNightNet }];
            }),
          ),
        },
      };
    });
  }, []);

  const deleteRoomType = useCallback(
    (categoryIndex: number, id: PackageOptionId) => {
      updateActiveOption((state) => {
        const totalRoomCount = state.roomsByCategory.reduce((total, rooms) => total + rooms.length, 0);
        if (totalRoomCount <= 1) {
          return state;
        }
        return {
          ...state,
          roomsByCategory: state.roomsByCategory.map((rooms, index) =>
            index === categoryIndex ? rooms.filter((room) => room.id !== id) : rooms,
          ),
        };
      });
    },
    [updateActiveOption],
  );

  const selectAsset = useCallback(
    (categoryIndex: number, id: PackageOptionId, asset: RoomAssetLink) => {
      updateCategoryRooms(categoryIndex, (rooms) =>
        rooms.map((room) => (room.id === id ? { ...room, assetId: asset.id, assetName: asset.name } : room)),
      );
    },
    [updateCategoryRooms],
  );

  const toggleAsiaOnly = useCallback(
    (categoryIndex: number, id: PackageOptionId): string | undefined => {
      const room = optionState.roomsByCategory[categoryIndex]?.find((candidate) => candidate.id === id);
      const enabling = room !== undefined && !room.asiaOnly;

      if (enabling && !optionState.lengthsOfStay.includes(ASIA_ONLY_LOS)) {
        return `Add a ${ASIA_ONLY_LOS}-night LOS before enabling Asia only`;
      }

      updateActiveOption((state) => ({
        ...state,
        roomsByCategory: state.roomsByCategory.map((rooms, index) =>
          index === categoryIndex
            ? rooms.map((candidate) =>
                candidate.id === id ? { ...candidate, asiaOnly: !candidate.asiaOnly } : candidate,
              )
            : rooms,
        ),
        pricingByRoomId:
          enabling && state.pricingByRoomId[id]
            ? { ...state.pricingByRoomId, [id]: clearNonAsiaOnlyPricing(state.pricingByRoomId[id]) }
            : state.pricingByRoomId,
      }));

      return undefined;
    },
    [optionState.lengthsOfStay, optionState.roomsByCategory, updateActiveOption],
  );

  const updateRoomPricing = useCallback(
    (id: PackageOptionId, defaultOccupancy: string, changes: Partial<RoomPricing>) => {
      updateActiveOption((state) => ({
        ...state,
        pricingByRoomId: {
          ...state.pricingByRoomId,
          [id]: { ...(state.pricingByRoomId[id] ?? defaultRoomPricing(defaultOccupancy)), ...changes },
        },
      }));
    },
    [updateActiveOption],
  );

  const setRoomSellPrice = useCallback(
    (id: PackageOptionId, defaultOccupancy: string, nights: number, value: string, audToNetRate = 1) => {
      updateActiveOption((state) => {
        const current = state.pricingByRoomId[id] ?? defaultRoomPricing(defaultOccupancy);
        const isAsiaOnly = state.roomsByCategory.some((rooms) => rooms.some((room) => room.id === id && room.asiaOnly));

        const priced = {
          ...current,
          ...setSellPrice(current, { isAsiaOnly, lengthsOfStay: state.lengthsOfStay, nights, value }),
        };

        const extraNightSell = deriveExtraNightSell(priced, state.lengthsOfStay);

        const pricingByRoomId = { ...state.pricingByRoomId, [id]: { ...priced, extraNightSell } };

        // Only the edited LOS re-derives; other LOS columns keep the net they already have.
        if (isRoomInNetFormulaScope(current)) {
          return {
            ...state,
            pricingByRoomId: applyNetFormulaToPricing(pricingByRoomId, {
              applyToExtraNight: !priced.extraNightSellEdited,
              lengthsOfStay: [nights],
              netPercent: current.netPercent ?? 0,
              audToNetRate,
              roomIds: [id],
            }),
          };
        }

        return { ...state, pricingByRoomId };
      });
    },
    [updateActiveOption],
  );

  const setRoomNetRate = useCallback(
    (id: PackageOptionId, defaultOccupancy: string, nights: number, value: string) => {
      updateActiveOption((state) => {
        const current = state.pricingByRoomId[id] ?? defaultRoomPricing(defaultOccupancy);
        return {
          ...state,
          pricingByRoomId: {
            ...state.pricingByRoomId,
            // A manual net opts the room out of the formula so later sell edits can't overwrite it.
            [id]: { ...current, netByLos: { ...current.netByLos, [nights]: value }, netPercent: undefined },
          },
        };
      });
    },
    [updateActiveOption],
  );

  const setNetRateFormula = useCallback(
    (netRateFormula: NetRateFormulaState) => {
      updateActiveOption((state) => ({ ...state, netRateFormula }));
    },
    [updateActiveOption],
  );

  // Sell is AUD, net is in the account currency; audToNetRate converts across (1 if AUD).
  const applyNetRateFormula = useCallback(
    (netPercent: number, applyToExtraNight: boolean, audToNetRate: number, roomIds: Array<PackageOptionId>) => {
      updateActiveOption((state) => ({
        ...state,
        pricingByRoomId: applyNetFormulaToPricing(state.pricingByRoomId, {
          applyToExtraNight,
          lengthsOfStay: state.lengthsOfStay,
          netPercent,
          audToNetRate,
          roomIds,
        }),
      }));
    },
    [updateActiveOption],
  );

  const reapplyNetFormulaAtRate = useCallback((rates: { fromRate: number; toRate: number }) => {
    setOptionStates((prev) => {
      let changed = false;
      const next = Object.fromEntries(
        Object.entries(prev).map(([optionIndex, state]) => {
          const pricingByRoomId = reapplyNetFormula(state.pricingByRoomId, state.lengthsOfStay, rates);
          if (pricingByRoomId === state.pricingByRoomId) {
            return [optionIndex, state];
          }
          changed = true;
          return [optionIndex, { ...state, pricingByRoomId }];
        }),
      );
      return changed ? next : prev;
    });
  }, []);

  // Paste never creates assets: rows with no matching or an ambiguous room asset are skipped.
  const applyPastedOffer = useCallback(
    (roomRows: Array<ParsedRoomRow>, assets: Array<RoomAssetLink>) => {
      if (roomRows.length === 0) {
        return;
      }
      updateActiveOption((state) => {
        const rooms = [...state.roomsByCategory[BASIC_CATEGORY_INDEX]];
        const pricingByRoomId = { ...state.pricingByRoomId };
        const linkedAssetIds = rooms.map((room) => room.assetId).filter(Boolean);
        const matches: Array<{ roomId: PackageOptionId; row: ParsedRoomRow }> = [];

        for (const row of roomRows) {
          const match = matchRoomAsset(row.name, assets, linkedAssetIds);
          if (!match || !('asset' in match)) {
            continue;
          }

          const { id: assetId, name: assetName } = match.asset;
          let room = rooms.find((candidate) => candidate.assetId === assetId);
          if (!room) {
            const blankIndex = rooms.findIndex((candidate) => !candidate.assetId);
            if (blankIndex >= 0) {
              room = { ...rooms[blankIndex], assetId, assetName };
              rooms[blankIndex] = room;
            } else {
              room = {
                asiaOnly: false,
                assetId,
                assetName,
                id: newPackageOptionId(),
              };
              rooms.push(room);
            }
          }

          matches.push({ roomId: room.id, row });
        }

        const pastedLos = matches.flatMap(({ row }) => Object.keys(row.pricesByLos).map(Number));
        const lengthsOfStay = [...new Set([...state.lengthsOfStay, ...pastedLos])].sort((a, b) => a - b);

        for (const { roomId, row } of matches) {
          const current = pricingByRoomId[roomId] ?? defaultRoomPricing('2A');
          const isAsiaOnly = rooms.find((room) => room.id === roomId)?.asiaOnly ?? false;
          const sellByLos = { ...current.sellByLos };
          const sellEditedByLos = { ...current.sellEditedByLos };

          for (const nights of lengthsOfStay.filter(
            (los) => row.pricesByLos[los] != null && (!isAsiaOnly || los === ASIA_ONLY_LOS),
          )) {
            sellByLos[nights] = String(row.pricesByLos[nights]);
            sellEditedByLos[nights] = true;
          }

          pricingByRoomId[roomId] = { ...current, sellByLos, sellEditedByLos };
        }

        return {
          ...state,
          lengthsOfStay,
          pricingByRoomId,
          roomsByCategory: state.roomsByCategory.map((categoryRooms, index) =>
            index === BASIC_CATEGORY_INDEX ? rooms : categoryRooms,
          ),
        };
      });
    },
    [updateActiveOption],
  );

  const replaceFirstOption = useCallback((state: OptionState) => {
    setOptionStates((prev) => ({ ...prev, 0: state }));
    setActiveOption(0);
  }, []);

  const duplicateCategoryRooms = useCallback(
    ({
      sourceCategoryIndex,
      targetCategoryIndex,
      assetIds,
      occupancy,
      suffix,
      optionIndexes,
      metadata,
      perLos,
      audToNetRate,
      extraNightPerLos = perLos,
    }: {
      sourceCategoryIndex: number;
      targetCategoryIndex: number;
      assetIds: Array<string>;
      occupancy?: string;
      suffix: string;
      optionIndexes?: Array<number>;
      metadata?: Pick<CategoryRoom, 'upliftType' | 'experiences' | 'propertyNetLift'>;
      perLos: (sell: number, net: number, nights: number) => { sell: number; net: number };
      audToNetRate: number;
      // Bundled experiences are a per-stay charge and must not leak onto extra nights; defaults to perLos.
      extraNightPerLos?: (sell: number, net: number, nights: number) => { sell: number; net: number };
    }) => {
      if (assetIds.length === 0) {
        return;
      }
      setOptionStates((prevOptionStates) => {
        const nextOptionStates = { ...prevOptionStates };
        for (const [optionKey, state] of Object.entries(prevOptionStates)) {
          if (optionIndexes && !optionIndexes.includes(Number(optionKey))) {
            continue;
          }
          const sourceRooms = state.roomsByCategory[sourceCategoryIndex].filter((room) =>
            assetIds.includes(room.assetId),
          );
          if (sourceRooms.length === 0) {
            continue;
          }

          const targetRooms = [...state.roomsByCategory[targetCategoryIndex]];
          const pricingByRoomId = { ...state.pricingByRoomId };

          for (const sourceRoom of sourceRooms) {
            const sourcePricing = state.pricingByRoomId[sourceRoom.id] ?? defaultRoomPricing('2A');
            const targetSuffix = [sourceRoom.suffix, suffix].filter(Boolean).join(' - ');
            const existingIndex = targetRooms.findIndex(
              (room) => room.assetId === sourceRoom.assetId && room.suffix === targetSuffix,
            );
            const targetRoom =
              existingIndex >= 0
                ? { ...targetRooms[existingIndex], suffix: targetSuffix, ...metadata }
                : {
                    id: newPackageOptionId(),
                    assetName: sourceRoom.assetName,
                    assetId: sourceRoom.assetId,
                    asiaOnly: sourceRoom.asiaOnly,
                    suffix: targetSuffix,
                    ...metadata,
                  };
            if (existingIndex >= 0) {
              targetRooms[existingIndex] = targetRoom;
            } else {
              targetRooms.push(targetRoom);
            }

            const sellByLos: Record<number, string> = {};
            const netByLos: Record<number, string> = {};
            // Marked as edited so addLos does not re-prorate the uplift away.
            const sellEditedByLos: Record<number, boolean> = {};
            for (const nights of state.lengthsOfStay) {
              const baseSell = Number(sourcePricing.sellByLos[nights]) || 0;
              const baseNet = Number(sourcePricing.netByLos[nights]) || 0;
              if (baseSell > 0 || baseNet > 0) {
                const derived = perLos(baseSell, baseNet, nights);
                sellByLos[nights] = String(derived.sell);
                netByLos[nights] = String(derived.net);
                sellEditedByLos[nights] = true;
              } else {
                sellByLos[nights] = '';
                netByLos[nights] = '';
              }
            }
            // Derives from the source room's LOS via extraNightPerLos, not the already-uplifted sellByLos,
            // so a per-stay experience charge (excluded from extraNightPerLos) doesn't leak onto extra nights.
            let extraNightSell = '';
            let extraNightNet = '';
            let extraNightSellEdited = false;
            const [baseLos] = state.lengthsOfStay;
            const baseSourceSell = Number(sourcePricing.sellByLos[baseLos]) || 0;
            const baseSourceNet = Number(sourcePricing.netByLos[baseLos]) || 0;
            const { sell: newBaseSell, net: newBaseNet } =
              baseSourceSell > 0 || baseSourceNet > 0
                ? extraNightPerLos(baseSourceSell, baseSourceNet, baseLos)
                : { sell: 0, net: 0 };
            if (newBaseSell > 0 || newBaseNet > 0) {
              // Extra-night net follows the base room's own net rate formula; without one, the new room's margin.
              const baseRoom = state.roomsByCategory[BASIC_CATEGORY_INDEX].find(
                (room) => room.assetId === sourceRoom.assetId,
              );
              const baseNetPercent = baseRoom && state.pricingByRoomId[baseRoom.id]?.netPercent;
              const netFraction = baseNetPercent ? (baseNetPercent / 100) * audToNetRate : undefined;
              const derived = extraNightFromNewBaseLos(newBaseSell, newBaseNet, baseLos, netFraction);
              extraNightSell = String(derived.sell);
              extraNightNet = String(derived.net);
              extraNightSellEdited = true;
            }

            pricingByRoomId[targetRoom.id] = {
              ...defaultRoomPricing(occupancy ?? sourcePricing.occupancy),
              allocation: sourcePricing.allocation,
              sellByLos,
              sellEditedByLos,
              netByLos,
              extraNightSell,
              extraNightSellEdited,
              extraNightNet,
            };
          }

          nextOptionStates[optionKey] = {
            ...state,
            roomsByCategory: state.roomsByCategory.map((rooms, index) =>
              index === targetCategoryIndex ? targetRooms : rooms,
            ),
            pricingByRoomId,
          };
        }
        return nextOptionStates;
      });
    },
    [],
  );

  const duplicateAsUpgrade = useCallback(
    ({
      assetIds,
      suffix,
      sellIncrease,
      netIncrease,
      audToNetRate = 1,
      optionIndexes,
      experiences,
      propertySellIncrease = 0,
    }: {
      assetIds: Array<string>;
      suffix: string;
      sellIncrease: number;
      netIncrease: number;
      audToNetRate?: number;
      optionIndexes?: Array<number>;
      experiences?: Array<AttachedExperience>;
      propertySellIncrease?: number;
    }) => {
      const hasExperiences = Boolean(experiences?.length);

      duplicateCategoryRooms({
        sourceCategoryIndex: BASIC_CATEGORY_INDEX,
        targetCategoryIndex: UPGRADE_CATEGORY_INDEX,
        assetIds,
        suffix,
        optionIndexes,
        metadata: {
          upliftType: resolveUpliftType(hasExperiences, propertySellIncrease, netIncrease),
          experiences,
          propertyNetLift: netIncrease,
        },
        perLos: upgradePerLos(sellIncrease, netIncrease),
        extraNightPerLos: hasExperiences ? upgradePerLos(propertySellIncrease, netIncrease) : undefined,
        audToNetRate,
      });
    },
    [duplicateCategoryRooms],
  );

  const duplicateAsSolo = useCallback(
    ({
      assetIds,
      suffix,
      occupancy,
      rateBasis,
      sellDecrease,
      netDecrease,
      audToNetRate = 1,
    }: {
      assetIds: Array<string>;
      suffix: string;
      occupancy: string;
      rateBasis: 'per_stay' | 'per_night';
      sellDecrease: number;
      netDecrease: number;
      audToNetRate?: number;
    }) => {
      const perLos = soloPerLos(rateBasis, sellDecrease, netDecrease);
      duplicateCategoryRooms({
        sourceCategoryIndex: BASIC_CATEGORY_INDEX,
        targetCategoryIndex: SOLO_CATEGORY_INDEX,
        assetIds,
        occupancy,
        suffix,
        perLos,
        audToNetRate,
      });
      duplicateCategoryRooms({
        sourceCategoryIndex: UPGRADE_CATEGORY_INDEX,
        targetCategoryIndex: SOLO_UPGRADE_CATEGORY_INDEX,
        assetIds,
        occupancy,
        suffix,
        perLos,
        audToNetRate,
      });
    },
    [duplicateCategoryRooms],
  );

  const setSelectedLos = useCallback(
    (nights: number) => {
      updateActiveOption((state) => ({ ...state, selectedLos: nights }));
    },
    [updateActiveOption],
  );

  const addLos = useCallback(
    (nights: number, audToNetRate = 1) => {
      updateActiveOption((state) => {
        if (nights < MIN_LOS || nights > MAX_LOS || state.lengthsOfStay.includes(nights)) {
          return state;
        }
        // Kept ascending: proration and extra-night anchor on the leftmost (shortest) LOS.
        const lengthsOfStay = [...state.lengthsOfStay, nights].sort((a, b) => a - b);
        const asiaOnlyRoomIds = new Set(
          state.roomsByCategory.flatMap((rooms) => rooms.filter((room) => room.asiaOnly).map((room) => room.id)),
        );
        const pricingByRoomId = Object.fromEntries(
          Object.entries(state.pricingByRoomId).map(([idKey, pricing]) => {
            const prorated = {
              ...pricing,
              ...prorateSellByLos(pricing, { isAsiaOnly: asiaOnlyRoomIds.has(idKey), lengthsOfStay }),
            };
            const net = netAtAnchorMargin(prorated, nights, lengthsOfStay);
            return [
              idKey,
              net === undefined ? prorated : { ...prorated, netByLos: { ...prorated.netByLos, [nights]: net } },
            ];
          }),
        );

        // Rooms may carry different netPercent values, so group by value and apply per group.
        const roomIdsByNetPercent = new Map<number, Array<PackageOptionId>>();
        for (const [idKey, pricing] of Object.entries(pricingByRoomId)) {
          if (!isRoomInNetFormulaScope(pricing)) {
            continue;
          }
          const netPercent = pricing.netPercent ?? 0;
          const roomIds = roomIdsByNetPercent.get(netPercent) ?? [];
          roomIds.push(idKey);
          roomIdsByNetPercent.set(netPercent, roomIds);
        }

        let nextPricingByRoomId = pricingByRoomId;
        for (const [netPercent, roomIds] of roomIdsByNetPercent) {
          nextPricingByRoomId = applyNetFormulaToPricing(nextPricingByRoomId, {
            applyToExtraNight: false,
            lengthsOfStay: [nights],
            netPercent,
            audToNetRate,
            roomIds,
          });
        }

        return {
          ...state,
          lengthsOfStay,
          pricingByRoomId: nextPricingByRoomId,
        };
      });
    },
    [updateActiveOption],
  );

  const deleteLos = useCallback(
    (nights: number) => {
      updateActiveOption((state) => {
        if (state.lengthsOfStay.length <= 1) {
          return state;
        }
        const remaining = state.lengthsOfStay.filter((value) => value !== nights);
        const dropLos = <T>(byLos: Record<number, T>) =>
          Object.fromEntries(Object.entries(byLos).filter(([los]) => Number(los) !== nights));
        const pricingByRoomId = Object.fromEntries(
          Object.entries(state.pricingByRoomId).map(([id, pricing]) => [
            id,
            {
              ...pricing,
              sellByLos: dropLos(pricing.sellByLos),
              sellEditedByLos: dropLos(pricing.sellEditedByLos),
              netByLos: dropLos(pricing.netByLos),
            },
          ]),
        );
        return {
          ...state,
          lengthsOfStay: remaining,
          pricingByRoomId,
          selectedLos: state.selectedLos === nights ? Math.min(...remaining) : state.selectedLos,
        };
      });
    },
    [updateActiveOption],
  );

  const addRoomAsset = useCallback((asset: CreatedAsset) => {
    setRoomAssets((prev) => (prev.some((existing) => existing.id === asset.id) ? prev : [...prev, asset]));
  }, []);

  const loadDraft = useCallback((draft: PackageOptionsDraft) => {
    setActiveOption(0);
    setOptionStates(draft.optionStates);
    setRoomAssets(draft.roomAssets);
  }, []);

  return {
    activeOption,
    setActiveOption,
    optionState,
    optionStates,
    roomAssets,
    addRoomAsset,
    addRoomType,
    copyFromFirstOption,
    deleteRoomType,
    selectAsset,
    toggleAsiaOnly,
    updateRoomPricing,
    setRoomSellPrice,
    setRoomNetRate,
    setNetRateFormula,
    applyNetRateFormula,
    reapplyNetFormulaAtRate,
    applyPastedOffer,
    replaceFirstOption,
    duplicateAsUpgrade,
    duplicateAsSolo,
    setSelectedLos,
    addLos,
    deleteLos,
    loadDraft,
  };
}
